import { Moon, Sun, Bell, RefreshCw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ShopSwitcher from "./ShopSwitcher";
import { usePageHeader } from "../contexts/PageHeaderContext";
import styles from "./AppShell.module.css";

const getInitials = (name = "") => {
  if (!name) return "ZT";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0]?.toUpperCase() || "";
  const second = parts[1]?.[0]?.toUpperCase() || "";
  return `${first}${second}` || "ZT";
};

const formatLastUpdated = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) return `Today, ${timeStr}`;

  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${dateStr}, ${timeStr}`;
};

const AppShellHeader = ({
  normalizedPath,
  showHeaderGreeting,
  activeChannelLabel,
  handleLogoClick,
  toggleDrawer,
  themeMode,
  toggleThemeMode,
  unreadCount,
  handleProfileClick,
  ownerName,
  t,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageHeader, clearPageHeader, triggerRefresh } = usePageHeader();

  // Clear stale header info whenever the route changes
  useEffect(() => {
    clearPageHeader();
  }, [location.pathname, clearPageHeader]);

  const lastUpdatedLabel = formatLastUpdated(pageHeader.updatedAt);

  return (
    <header className={styles.headerOuter}>
      <div className={styles.headerInner}>
        <div className={styles.headerSlotLeft}>
          <button
            type="button"
            className={styles.hamburgerButton}
            onClick={toggleDrawer}
            aria-label={t("AppShell.openMenu") || "Open menu"}
          >
            <img
              src="/assets/darkGreyHamburger.png"
              alt="Menu"
              width={24}
              height={24}
              className={styles.themeAwareIcon}
            />
          </button>
        </div>

        <div
          className={`${styles.headerSlotCenter} ${showHeaderGreeting ? styles.headerSlotCenterWithGreeting : ""}`}
        >
          {pageHeader.title ? (
            <div className={styles.headerChannelTitle}>
              <span className={styles.headerChannelLabel}>{pageHeader.title}</span>
            </div>
          ) : normalizedPath === "/" ? (
            <div className={styles.headerChannelTitle}>
              <span className={styles.headerChannelHash}>#</span>
              <span className={styles.headerChannelLabel}>
                {activeChannelLabel}
              </span>
            </div>
          ) : null}
          {normalizedPath === "/" && (
            <div id="header-tabs-slot" className={styles.headerTabsSlot} />
          )}
          <img
            src="/assets/zeqi_black_clearBG.png"
            alt="Zeqi"
            className={`${styles.brandLogo} ${styles.headerLogoMobile} ${styles.themeAwareLogo}`}
            onClick={handleLogoClick}
            role="button"
            aria-label={t("AppShell.goHomeAria") || "Go to home"}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogoClick();
            }}
          />
        </div>

        <div className={styles.headerSlotRight}>
          <ShopSwitcher />

          {lastUpdatedLabel && (
            <div className={styles.headerPageMeta}>
              <span className={styles.headerLastUpdated}>
                Last updated: {lastUpdatedLabel}
              </span>
              {pageHeader.hasRefresh && (
                <button
                  type="button"
                  className={`${styles.headerRefreshBtn} ${
                    pageHeader.refreshing ? styles.headerRefreshBtnSpinning : ""
                  }`}
                  onClick={triggerRefresh}
                  disabled={pageHeader.refreshing}
                  title="Refresh report"
                  aria-label="Refresh report"
                >
                  <RefreshCw size={13} strokeWidth={2} />
                </button>
              )}
              {pageHeader.refreshError ? (
                <span className={styles.headerRefreshError}>
                  {pageHeader.refreshError}
                </span>
              ) : null}
            </div>
          )}

          <button
            type="button"
            className={styles.themeToggle}
            onClick={toggleThemeMode}
            aria-label={
              themeMode === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
            title={
              themeMode === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
            }
          >
            {themeMode === "dark" ? (
              <Sun size={18} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Moon size={18} strokeWidth={2} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className={`${styles.notificationButton} ${styles.headerMobileOnly} ${
              unreadCount ? styles.notificationButtonActive : ""
            }`}
            onClick={() => navigate("/notifications")}
            aria-label={t("AppShell.notificationsTitle") || "Notifications"}
          >
            <Bell size={30} aria-hidden="true" />
            {unreadCount > 0 && (
              <span className={styles.badge}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.profileButton} ${styles.headerMobileOnly}`}
            onClick={handleProfileClick}
            aria-label={t("AppShell.userSettings") || "Profile"}
          >
            <span className={styles.profilePill}>
              {getInitials(ownerName)}
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AppShellHeader;
